import { Component, EventEmitter, Input, OnInit, Output, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SystemService } from '../../services/system.service';
import { InstrumentService } from '../instrument/instrument.service';
import { TranslateService } from '@ngx-translate/core';
import { ICC } from '../../interfaces/interfaces';
import { UnitsService } from '../../services/units.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

@Component({
    selector: 'app-icc',
    templateUrl: './icc.component.html',
    styleUrls: ['./icc.component.scss']
})

export class ICCComponent implements OnInit {
    public iccFormGroup: FormGroup;
    public icc: ICC;
    @Input() title: string;
    @Output()
    entityChanged: EventEmitter<any> = new EventEmitter();

    // Nuevas propiedades para manejo de magnitudes
    magnitudCtrl = new FormControl();
    filteredMagnitudes: Observable<string[]>;
    selectedMagnitudes: string[] = [];
    allMagnitudes: string[] = [];
    separatorKeysCodes: number[] = [ENTER, COMMA];

    @ViewChild('magnitudInput') magnitudInput: ElementRef<HTMLInputElement>;

    constructor(
        private _formBuilder: FormBuilder,
        private _matSnackBar: MatSnackBar,
        public _systemService: SystemService,
        public _instrumentService: InstrumentService,
        public translate: TranslateService,
        private _unitsService: UnitsService
    ) {
        // Configurar el observable para filtrar magnitudes
        this.filteredMagnitudes = this.magnitudCtrl.valueChanges.pipe(
            startWith(null),
            map((magnitud: string | null) => magnitud ? this._filter(magnitud) : this.allMagnitudes.slice())
        );
    }

    ngOnInit(): void {
        this.translate.use(this._systemService.visibility.language);
        // Cargar todas las magnitudes disponibles
        this.allMagnitudes = this._unitsService.getMagnitudeNames();
    }

    private _filter(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.allMagnitudes.filter(magnitud =>
            magnitud.toLowerCase().includes(filterValue) &&
            !this.selectedMagnitudes.includes(magnitud)
        );
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value && this.allMagnitudes.includes(value) && !this.selectedMagnitudes.includes(value)) {
            this.selectedMagnitudes.push(value);
            this.updateFormControl();
        }
        // Reset the input value
        if (event.input) {
            event.input.value = '';
        }
        this.magnitudCtrl.setValue(null);
    }

    remove(magnitud: string): void {
        const index = this.selectedMagnitudes.indexOf(magnitud);
        if (index >= 0) {
            this.selectedMagnitudes.splice(index, 1);
            this.updateFormControl();
        }
    }

    selectMagnitud(event: MatAutocompleteSelectedEvent): void {
        const value = event.option.viewValue;
        if (!this.selectedMagnitudes.includes(value)) {
            this.selectedMagnitudes.push(value);
            this.updateFormControl();
        }
        this.magnitudInput.nativeElement.value = '';
        this.magnitudCtrl.setValue(null);
    }

    private updateFormControl(): void {
        if (this.iccFormGroup) {
            this.iccFormGroup.patchValue({
                posibleMagnitudes: this.selectedMagnitudes.join(', ')
            });
        }
    }

    selectEntity(entity): void {
        this.icc = entity ? new ICC(entity) : new ICC();
        this.iccFormGroup = this.createICCForm();

        // Cargar magnitudes seleccionadas desde el objeto ICC
        if (this.icc.posibleMagnitudes) {
            this.selectedMagnitudes = this.icc.posibleMagnitudes
                .split(/[,;]/)
                .map(mag => mag.trim())
                .filter(mag => mag.length > 0);
        } else {
            this.selectedMagnitudes = [];
        }
    }

    deleteICC(): void {
        if (!this.icc?.id) {
            return;
        }
        this._instrumentService.deleteICC(this.icc.id).subscribe(
            () => {
                this._matSnackBar.open('ICC eliminado', 'OK', { verticalPosition: 'top', duration: 4000 });
                this.entityChanged.emit(this.icc.id);
                this.selectEntity(null);
            },
            error => {
                this._matSnackBar.open('Error al eliminar el ICC', 'OK', { verticalPosition: 'top', duration: 4000 });
            }
        );
    }

    guardarICC(): void {
        const iccToSave = this.iccFormGroup.getRawValue();
        this._instrumentService.saveICC(iccToSave).subscribe(
            resp => {
                this._matSnackBar.open('ICC guardado', 'OK', { verticalPosition: 'top', duration: 4000 });
                this.entityChanged.emit(resp);
                this.selectEntity(resp);
            }
        );
    }

    // Método público para ser llamado desde el componente padre
    selected(entity): void {
        this.selectEntity(entity);
    }

    createICCForm(): FormGroup {
        return this._formBuilder.group({
            id: [this.icc.id],
            name: [this.icc.name],
            description: [this.icc.description],
            dataFormat: [this.icc.dataFormat],
            updateDate: [this.icc.updateDate],
            version: [this.icc.version],
            procedure: [this.icc.procedure],
            filePath: [this.icc.filePath],
            posibleMagnitudes: [this.icc.posibleMagnitudes],
            iccType: [this.icc.iccType || ''],
            aiDescription: [this.icc.aiDescription || ''],
            hasCalibrator: [this.icc.hasCalibrator || false],
            calibrators: [this.icc.calibrators || '']
        });
    }
}
